import React, { useLayoutEffect, useState, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Fade, IconButton, Paper, Popper, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

// Guides showing right now. A guide that waits for its turn stays hidden
// while any other guide shows, so the user never sees two at once.
const shownGuides = new Set();
const guideListeners = new Set();

function subscribeToGuides(listener) {
    guideListeners.add(listener);
    return () => guideListeners.delete(listener);
}

function anotherGuideShown() {
    return shownGuides.size > 0;
}

/**
 * Registers a visible guide drawn without GuideCallout, so guides that wait
 * for their turn stay hidden while it shows.
 */
export function useGuideShown(shown) {
    // A layout effect, so a waiting guide hides before the browser paints.
    useLayoutEffect(() => {
        if (!shown) return undefined;
        const token = {};
        shownGuides.add(token);
        guideListeners.forEach((listener) => listener());
        return () => {
            shownGuides.delete(token);
            guideListeners.forEach((listener) => listener());
        };
    }, [shown]);
}

/** One choice inside a guide: an icon tile, a label and a short detail. */
export function GuideCalloutOption({ icon, label, detail, onClick, disabled = false }) {
    return (
        <ButtonBase
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1,
                borderRadius: 1.5,
                textAlign: 'left',
                justifyContent: 'flex-start',
                '&:hover': { bgcolor: 'action.hover' },
                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
                '&.Mui-disabled': { opacity: 0.5 },
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    width: 34,
                    height: 34,
                    flex: '0 0 auto',
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    color: 'text.primary',
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{label}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.35 }}>{detail}</Typography>
            </Box>
        </ButtonBase>
    );
}

GuideCalloutOption.propTypes = {
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    detail: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

/**
 * The speech bubble that guides a new user from a top-bar control. Every guide
 * uses this one shape, pointer, palette, type, shadow, close control and
 * entrance animation; only the anchor and the content differ.
 */
export default function GuideCallout({
    open,
    anchorEl,
    ariaLabel,
    title,
    subtitle = null,
    onDismiss = null,
    waitForOtherGuides = false,
    children = null,
}) {
    const [arrowEl, setArrowEl] = useState(null);
    const otherGuideShown = useSyncExternalStore(subscribeToGuides, anotherGuideShown);
    const ready = Boolean(open && anchorEl);
    useGuideShown(ready && !waitForOtherGuides);

    if (!ready || (waitForOtherGuides && otherGuideShown)) return null;

    return (
        <Popper
            open
            anchorEl={anchorEl}
            placement="bottom-end"
            transition
            modifiers={[
                { name: 'offset', options: { offset: [0, 14] } },
                { name: 'arrow', enabled: true, options: { element: arrowEl } },
                { name: 'preventOverflow', options: { padding: 12 } },
            ]}
            sx={{ zIndex: 1150 }}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={250}>
                    <Box
                        sx={{
                            animation: `${float} 3.2s ease-in-out infinite`,
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                        }}
                    >
                        <Paper
                            role="dialog"
                            aria-label={ariaLabel}
                            elevation={8}
                            sx={{
                                position: 'relative',
                                width: 300,
                                maxWidth: 'calc(100vw - 24px)',
                                p: 1.5,
                                pt: 1.25,
                                borderRadius: 2.5,
                                border: '1px solid',
                                borderColor: 'primary.main',
                            }}
                        >
                            <Box
                                ref={setArrowEl}
                                aria-hidden="true"
                                sx={{
                                    position: 'absolute',
                                    top: -8,
                                    width: 14,
                                    height: 14,
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: 0,
                                        bgcolor: 'background.paper',
                                        borderTop: '1px solid',
                                        borderLeft: '1px solid',
                                        borderColor: 'primary.main',
                                        transform: 'rotate(45deg)',
                                    },
                                }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                                <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 750, lineHeight: 1.3 }}>
                                        {title}
                                    </Typography>
                                    {subtitle && (
                                        <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }}>
                                            {subtitle}
                                        </Typography>
                                    )}
                                </Box>
                                {onDismiss && (
                                    <IconButton size="small" aria-label="Dismiss" onClick={onDismiss} sx={{ mt: -0.25 }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                            {children}
                        </Paper>
                    </Box>
                </Fade>
            )}
        </Popper>
    );
}

GuideCallout.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    ariaLabel: PropTypes.string.isRequired,
    title: PropTypes.node.isRequired,
    subtitle: PropTypes.node,
    onDismiss: PropTypes.func,
    waitForOtherGuides: PropTypes.bool,
    children: PropTypes.node,
};
